import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  TicketFilterOptionsSelector,
  TicketsLoadingStateSelector,
  TicketsSelector,
} from 'store/tickets/selectors';
import { useAppSelector } from 'store/hooks';
import { PortalConfigSelector } from 'store/config/selectors';
import { UserRoleSelector } from 'store/user/selector';

import { Roles } from 'constants/roles';
import { TICKET } from 'router/routes';

import SimpleTable from 'components/SimpleTable';
import { TableTypes } from 'components/Table/constants';
import EllipsisTooltip from 'components/EllipsisTooltip/EllipsisTooltip';
import TicketTitle from './TicketTitle';
import { ReactComponent as ChatsIcon } from 'assets/chatsIcon.svg';
import { ReactComponent as TimelineIcon } from 'assets/timelineIcon.svg';

import { PsaTicketFieldNames } from 'services/tickets/constants';
import { ITicket } from 'services/tickets/types';

import useStyles from '../../../styles';
import StatusChip from '../../../../../components/StatusChip';
import { Divider } from '@mui/material';

const TicketsTable: React.FC = () => {
  const classes = useStyles();
  const tickets = useAppSelector(TicketsSelector);
  const isTicketsLoading = useAppSelector(TicketsLoadingStateSelector);
  const config = useAppSelector(PortalConfigSelector);
  const { closeStatusId } = useAppSelector(TicketFilterOptionsSelector);
  const role = useAppSelector(UserRoleSelector);
  const isAdminManagerRole = role === Roles.Admin || role === Roles.Manager;
  const navigate = useNavigate();

  const columns = useMemo(() => {
    const clm = [
      {
        name: 'Ticket ID',
        key: 'id',
        width: 150,
        minWidth: 150,
        type: TableTypes.custom,
        accessor: (row: ITicket) => <TicketTitle ticket={row} />,
      },
      {
        name: 'Summary',
        key: 'title',
        minWidth: 200,
        width: 300,
      },
      {
        name: 'Contact',
        key: PsaTicketFieldNames[config.psaType].contact,
        minWidth: 200,
      },
      {
        name: 'Customer',
        key: 'company',
        type: TableTypes.custom,
        accessor: (row: ITicket) => {
          const customerName = row.siteName
            ? `${row[PsaTicketFieldNames[config.psaType].company as 'company']}/${row.siteName}`
            : row[PsaTicketFieldNames[config.psaType].company as 'company'];
          return <EllipsisTooltip text={customerName || ''} />;
        },
        minWidth: 100,
        width: 200,
      },
      {
        name: 'Status',
        key: 'status',
        type: TableTypes.custom,
        width: 200,
        minWidth: 200,
        accessor: (row: ITicket) => <StatusChip text={row.status} status={row.status} />,
      },
      {
        name: 'Action',
        key: 'action',
        type: TableTypes.custom,
        accessor: (row: ITicket) => (
          <div className={classes.chatButtonWrapper}>
            <TimelineIcon
              onClick={() => {
                navigate(TICKET.replace(':ticketId', `${row.id}?defaultButtonGroupValue=timeline`));
              }}
            />
            <Divider orientation="vertical" variant="middle" flexItem />
            <ChatsIcon
              onClick={() => {
                navigate(TICKET.replace(':ticketId', `${row.id}?defaultButtonGroupValue=chat`));
              }}
            />
          </div>
        ),
        width: 75,
        minWidth: 75,
      },
    ];
    if (config.disableChats) {
      clm.splice(-1);
    }
    return clm.filter(c => {
      if (isAdminManagerRole) return true;
      return !isAdminManagerRole && c.key !== 'company';
    });
  }, [closeStatusId, config, classes]);

  return (
    <SimpleTable
      data={tickets}
      columns={columns}
      loading={isTicketsLoading}
      className={classes.table}
    />
  );
};

export default TicketsTable;
